import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Box, Tab, Tabs, Typography } from "@mui/material";

// A settings page made of named tabs. The active tab is kept in the URL as
// ?tab=<key>, so a tab can be linked to directly and survives a reload.
//
//   <TabbedPage id="providers" title={...} tabs={[{ key, label, render }]} />
//
// defaultTab names the tab to open when the URL carries none, so an old
// route such as /admin/eprs can land on the right tab of its new page.
const TabbedPage = ({ id, title, intro, tabs, defaultTab, isDarktheme }) => {
  const history = useHistory();
  const location = useLocation();
  const keys = tabs.map((t) => t.key);

  const fromUrl = () => {
    const wanted = new URLSearchParams(location.search).get("tab") || defaultTab;
    const index = keys.indexOf(wanted);
    return index >= 0 ? index : 0;
  };

  const [value, setValue] = useState(fromUrl);

  useEffect(() => {
    setValue(fromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const select = (event, index) => {
    setValue(index);
    history.replace({ pathname: location.pathname, search: `?tab=${keys[index]}` });
  };

  const current = tabs[value] || tabs[0];

  return (
    <Box sx={{ color: isDarktheme ? "#fff" : "inherit" }}>
      <Box sx={{ padding: "16px 24px 0" }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {intro && (
          <Typography variant="body2" sx={{ opacity: 0.75, marginTop: 0.5, maxWidth: "70ch" }}>
            {intro}
          </Typography>
        )}
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: 1 }}>
        <Tabs
          className={!isDarktheme ? "SettingsTabs" : "dark-SettingsTabs"}
          value={value}
          onChange={select}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((t) => (
            <Tab
              key={t.key}
              className={!isDarktheme ? "SettingsTab" : "dark-SettingsTab"}
              label={t.label}
              id={`${id}-tab-${t.key}`}
              aria-controls={`${id}-panel-${t.key}`}
            />
          ))}
        </Tabs>
      </Box>
      <Box role="tabpanel" id={`${id}-panel-${current.key}`} aria-labelledby={`${id}-tab-${current.key}`}>
        {current.render()}
      </Box>
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(TabbedPage);
